import defaultReducers from '@models/defaultReducers.ts'
import { NoraStoreState } from '@screens/Nora/models/nora.types.ts'
import {
  ChatbotConversation,
  ChatbotConversationCollection,
  ChatbotQuickPrompt,
  ChatbotQuickPromptCollection,
  ChatbotSettings,
  HealthDataReport,
  HealthDataReportCollection,
} from '@src/types.ts'

export const reducers = {
  ...defaultReducers,
  setRecentConversations: (
    state: NoraStoreState,
    { payload }: { payload: ChatbotConversationCollection },
  ) => {
    return {
      ...state,
      recentConversations: payload.conversations,
    }
  },
  setConversationsList: (
    state: NoraStoreState,
    { payload }: { payload: ChatbotConversationCollection },
  ) => {
    return {
      ...state,
      conversationsList: payload.conversations,
    }
  },
  appendConversationsList: (
    state: NoraStoreState,
    { payload }: { payload: ChatbotConversationCollection },
  ) => {
    const existing = new Set(state.conversationsList.map((conversation) => conversation.id))
    return {
      ...state,
      conversationsList: [
        ...state.conversationsList,
        ...payload.conversations.filter((conversation) => !existing.has(conversation.id)),
      ],
    }
  },
  setPinnedConversations: (
    state: NoraStoreState,
    { payload }: { payload: ChatbotConversationCollection },
  ) => {
    return {
      ...state,
      pinnedConversations: payload.conversations,
      pinnedConversationsCount: payload.totalCount,
    }
  },
  upsertChatbotConversation: (
    state: NoraStoreState,
    { payload }: { payload: ChatbotConversation },
  ) => {
    const exists = state.recentConversations.some((conversation) => conversation.id === payload.id)
    return {
      ...state,
      recentConversations: exists
        ? state.recentConversations.map((conversation) =>
            conversation.id === payload.id ? payload : conversation,
          )
        : [payload, ...state.recentConversations],
      conversationsList: state.conversationsList.map((conversation) =>
        conversation.id === payload.id ? payload : conversation,
      ),
    }
  },
  removeChatbotConversation: (
    state: NoraStoreState,
    { payload }: { payload: ChatbotConversation },
  ) => {
    return {
      ...state,
      recentConversations: state.recentConversations.filter(
        (conversation) => conversation.id !== payload.id,
      ),
      conversationsList: state.conversationsList.filter(
        (conversation) => conversation.id !== payload.id,
      ),
    }
  },
  setChatbotQuickPrompts: (
    state: NoraStoreState,
    { payload }: { payload: ChatbotQuickPromptCollection },
  ) => {
    return {
      ...state,
      chatbotQuickPrompts: payload.quickPrompts,
    }
  },
  insertChatbotQuickPrompt: (
    state: NoraStoreState,
    { payload }: { payload: ChatbotQuickPrompt },
  ) => {
    return {
      ...state,
      chatbotQuickPrompts: [payload, ...state.chatbotQuickPrompts],
    }
  },
  updateChatbotQuickPrompts: (
    state: NoraStoreState,
    { payload }: { payload: ChatbotQuickPrompt },
  ) => {
    return {
      ...state,
      chatbotQuickPrompts: state.chatbotQuickPrompts.map((quickPrompt) =>
        quickPrompt.id === payload.id ? payload : quickPrompt,
      ),
    }
  },
  removeChatbotQuickPrompt: (
    state: NoraStoreState,
    { payload }: { payload: ChatbotQuickPrompt },
  ) => {
    return {
      ...state,
      chatbotQuickPrompts: state.chatbotQuickPrompts.filter(
        (quickPrompt) => quickPrompt.id !== payload.id,
      ),
    }
  },
  setHealthDataReports: (
    state: NoraStoreState,
    { payload }: { payload: HealthDataReportCollection },
  ) => {
    return {
      ...state,
      healthDataReports: payload.reports,
    }
  },
  updateHealthDataReport: (state: NoraStoreState, { payload }: { payload: HealthDataReport }) => {
    return {
      ...state,
      healthDataReports: state.healthDataReports.map((report) =>
        report.id === payload.id ? payload : report,
      ),
    }
  },
  insertHealthDataReport: (state: NoraStoreState, { payload }: { payload: HealthDataReport }) => {
    return {
      ...state,
      healthDataReports: [payload, ...state.healthDataReports],
    }
  },
  setChatbotReportsEligible: (
    state: NoraStoreState,
    { payload }: { payload: { chatbotReportsEligible: boolean } },
  ) => {
    return {
      ...state,
      chatbotReportsEligible: payload.chatbotReportsEligible,
    }
  },
  setChatbotSettings: (state: NoraStoreState, { payload }: { payload: ChatbotSettings }) => {
    return {
      ...state,
      chatbotSettings: payload,
    }
  },
}

export default reducers
